// src/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../logo_colored_transp_no_text.png'; 
import LoginForm from './LoginForm'; 

// --- MÓDOSÍTOTT IMPORTOK: A központi firebase.js-ből használjuk az auth-ot és db-t ---
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from '../firebase';
import { ALLOWED_UIDS } from '../constants/Whitelist'; // ÚJ IMPORT

const Navbar = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null); 
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && ALLOWED_UIDS.includes(user.uid)) { // FRISSÍTVE: Csak ha a fehérlistán van
        setCurrentUser(user);
        try {
          // 1. ELŐSZÖR MEGNÉZZÜK A "Clubs" GYŰJTEMÉNYBEN
          const clubRef = doc(db, "Clubs", user.uid);
          const clubSnap = await getDoc(clubRef);

          if (clubSnap.exists()) {
            const data = clubSnap.data();
            setUserData({
              ...data,
              displayName: data.clubName || data.fullName || "Club",
              type: 'club' // Kényszerítjük a klub típust
            });
          } else {
            // 2. HA NINCS A CLUBS-BAN, MEGNÉZZÜK A "users" GYŰJTEMÉNYBEN
            const userRef = doc(db, "users", user.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const data = userSnap.data();
              // Megnézzük, hátha itt is 'club' a role mezője
              const isClub = data.role?.toLowerCase() === 'club';
              
              setUserData({
                ...data,
                displayName: isClub 
                  ? (data.clubName || data.fullName || "Club")
                  : (data.name || data.displayName || "My Profile"),
                type: isClub ? 'club' : 'competitor'
              });
            } else {
              setUserData({ displayName: "Profile", type: 'unknown' });
            }
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          setUserData({ displayName: "Error", type: 'error' });
        }
      } else {
        setCurrentUser(null);
        setUserData(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // NAVIGÁCIÓS FÜGGVÉNY
  const handleProfileNavigation = () => {
    if (!userData) return;

    // Szigorú ellenőrzés a beállított típus alapján
    if (userData.type === 'club') {
      navigate('/egyesulet-dashboard');
    } else {
      navigate('/profil');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Kijelentkezési hiba:", error);
    }
  };

  const buttonStyle = {
    backgroundColor: 'white',
    color: '#b30000',
    padding: '0.4rem 0.8rem',
    borderRadius: '5px',
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    transition: 'all 0.3s ease',
    border: 'none',
    cursor: 'pointer',
    marginLeft: '10px',
    display: 'inline-block'
  };

  const userPageButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    border: '1px solid white',
    opacity: userData ? 1 : 0.7,
    cursor: userData ? 'pointer' : 'wait'
  };

  // --- LOGÓ KONTÉNER STÍLUSA (KÖZÉPRE IGAZÍTVA) ---
  const logoContainerStyle = {
    backgroundColor: 'white',
    padding: '6px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s ease',
    marginLeft: '5px',
    lineHeight: 0, // Kiszűri a szöveg alapú eltolódást
    overflow: 'hidden'
  };

  const handleMouseEnter = (e, isUserBtn) => {
    e.target.style.transform = 'scale(1.05)';
    if (!isUserBtn) e.target.style.backgroundColor = '#f2f2f2';
    else e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
  };

  const handleMouseLeave = (e, isUserBtn) => {
    e.target.style.transform = 'scale(1)';
    if (!isUserBtn) e.target.style.backgroundColor = 'white';
    else e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
  };

  return (
    <>
      <nav
        className="no-print"
        style={{
          backgroundColor: '#b30000',
          padding: '0.5rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: 'white',
          fontFamily: 'Arial, sans-serif',
          position: 'relative',
          zIndex: 100
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* LOGÓ FEHÉR HÁTTÉRRAL */}
          <div 
            style={logoContainerStyle} 
            onClick={() => navigate('/')}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <img
              src={logo}
              alt="Archery Nexus Logó"
              style={{ 
                height: '50px', // Kicsit kisebbre vettem, hogy kényelmesen elférjen
                width: 'auto', 
                display: 'block',
                marginTop: '6px' // <--- Ezt a számot növeld (pl. 5px, 6px), ha még lejjebb akarod tolni
              }} 
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
             <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', cursor: 'pointer' }} onClick={() => navigate('/')}>
              Archery Nexus
            </span>
            <div style={{ display: 'flex', gap: '1rem', fontWeight: 'bold' }}>
              {currentUser && <Link to="/versenyek" style={{ color: 'white', textDecoration: 'none' }}>Versenyek</Link>}
              <Link to="/kapcsolat" style={{ color: 'white', textDecoration: 'none' }}>Kapcsolat</Link>
              <Link to="/adatvedelem" style={{ color: 'white', textDecoration: 'none' }}>Adatvédelem</Link>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {currentUser ? (
            <>
              <button
                onClick={handleProfileNavigation}
                style={userPageButtonStyle}
                onMouseEnter={(e) => handleMouseEnter(e, true)}
                onMouseLeave={(e) => handleMouseLeave(e, true)}
                disabled={!userData}
              >
                🏹 {userData ? userData.displayName : "Betöltés..."}
              </button>

              <button
                onClick={handleLogout}
                style={buttonStyle}
                onMouseEnter={(e) => handleMouseEnter(e, false)}
                onMouseLeave={(e) => handleMouseLeave(e, false)}
              >
                Kijelentkezés
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowLogin(true)}
              style={buttonStyle}
              onMouseEnter={(e) => handleMouseEnter(e, false)}
              onMouseLeave={(e) => handleMouseLeave(e, false)}
            >
              👤 Bejelentkezés
            </button>
          )}
        </div>
      </nav>

      {showLogin && <LoginForm onClose={() => setShowLogin(false)} />}
    </>
  );
};

export default Navbar;